import React, { useState, useEffect } from 'react'
import { Table, Input, DatePicker, Button } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import axios from 'axios'
import { GetStudentAttendedList } from '../../../AllAPI/AdminAPI'
import { toast } from 'react-toastify'
import * as XLSX from 'xlsx'

const AttendedStudentList = () => {
  const storedBranchId = sessionStorage.getItem('selected_branch')
  const BoardingYear = sessionStorage.getItem('BoardingYear')

  const [data, setData] = useState([])
  const [searchText, setSearchText] = useState('')
  const [loading, setLoading] = useState(false)

  // Date Filters
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)

  // Fetch API with optional dates
  const fetchAttendedStudentList = async () => {
    setLoading(true)
    if (!fromDate || !toDate) {
      toast.warning('Please select both From and To dates')
      return
    }

    const start = fromDate.format('YYYY-MM-DD')
    const end = toDate.format('YYYY-MM-DD')

    const objData = {
      fromDate: start,
      toDate: end,
    }

    try {
      const res = await axios.post(
        `${GetStudentAttendedList}/${storedBranchId}/${BoardingYear}`,
        objData,
      )

      if (!res.data || res.data.length === 0) {
        toast.warn('Data not available!')
        setData([])
        return
      }

      const formatted = res.data.map((item, index) => ({
        key: item.reg_id,
        index: index + 1,
        full_name: item.student?.full_name,
        wh_mobile: item.student?.wh_mobile,
        date: item.date || '-',
        morning_status:
          item.morning_status == 1
            ? 'Present'
            : item.morning_status == 2
            ? 'Absent'
            : '-',
        night_status:
          item.night_status == 1
            ? 'Present'
            : item.night_status == 2
            ? 'Absent'
            : '-',
        full_data: item,
      }))

      setData(formatted)
    } catch (err) {
      toast.error('Failed to load student list')
    } finally {
      setLoading(false)
    }
  }

  // Search Filter
  const filteredData = data.filter((item) => {
    const search = searchText.toLowerCase()

    return (
      (item.full_name ?? '').toLowerCase().includes(search) ||
      (item.wh_mobile ?? '').toString().toLowerCase().includes(search)
    )
  })

  const statusStyle = (value) => {
    if (value === 'Present') {
      return {
        background: '#d4fcd4',
        border: '1px solid #30c230',
        padding: '3px 8px',
        borderRadius: '10px',
        textAlign: 'center',
      }
    }
    if (value === 'Absent') {
      return {
        background: '#ffd6d6',
        border: '1px solid #ff4d4f',
        padding: '3px 8px',
        borderRadius: '10px',
        textAlign: 'center',
      }
    }
    return {}
  }

  const columns = [
    { title: 'Sr No', dataIndex: 'index', width: 80 },
    { title: 'Full Name', dataIndex: 'full_name' },
    { title: 'Mobile No.', dataIndex: 'wh_mobile' },
    { title: 'Date', dataIndex: 'date' },
    {
      title: 'Morning Status',
      dataIndex: 'morning_status',
      render: (value) => <span style={statusStyle(value)}>{value}</span>,
    },
    {
      title: 'Night Status',
      dataIndex: 'night_status',
      render: (value) => <span style={statusStyle(value)}>{value}</span>,
    },
  ]

  const exportToExcel = () => {
    if (filteredData.length === 0) {
      toast.warning('No data available to export')
      return
    }

    const excelData = filteredData.map((item, index) => ({
      'Sr No': index + 1,
      'Student Name': item.full_name,
      'Mobile No': item.wh_mobile,
      'Attendance Date': item.date,
      'Morning Status': item.morning_status,
      'Night Status': item.night_status,
    }))

    const worksheet = XLSX.utils.json_to_sheet(excelData)

    worksheet['!cols'] = [
      { wch: 8 },
      { wch: 35 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
    ]

    const workbook = XLSX.utils.book_new()

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Attendance')

    XLSX.writeFile(
      workbook,
      `Attended_Student_List_${fromDate?.format('YYYY-MM-DD') || ''}_to_${
        toDate?.format('YYYY-MM-DD') || ''
      }.xlsx`,
    )
  }

  return (
    <div className="form-container mainContainer">
      <div className="row p-0 m-0 pb-5">
        <div className="col-12 col-md-2"></div>

        <div className="col-12 col-md-8">
          <div className="header-row">
            <h2 className="page-title">Attended Student List</h2>
          </div>

          {/* Search Bar */}
          <div className="row align-items-center mb-3">
            <div className="col-12 col-md-8">
              <div className="row">
                <div className="col-12 col-md-4 mt-2">
                  <DatePicker
                    placeholder="From Date"
                    className="w-100"
                    onChange={(date) => setFromDate(date)}
                  />
                </div>

                <div className="col-12 col-md-4 mt-2">
                  <DatePicker
                    placeholder="To Date"
                    className="w-100"
                    onChange={(date) => setToDate(date)}
                  />
                </div>

                <div className="col-12 col-md-2 mt-2">
                  <Button
                    className="addButton w-100"
                    onClick={fetchAttendedStudentList}
                  >
                    Show
                  </Button>
                </div>

                <div className="col-12 col-md-2 mt-2">
                  <Button
                    type="primary"
                    onClick={exportToExcel}
                    style={{
                      backgroundColor: '#198754',
                      borderColor: '#198754',
                      width: '100%',
                    }}
                  >
                    Export Excel
                  </Button>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-4 text-end mt-2">
              <Input
                placeholder="Search..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 250 }}
              />
            </div>
          </div>
          {/* Table */}
          <Table
            columns={columns}
            dataSource={filteredData}
            pagination={{ pageSize: 10 }}
            bordered
            loading={loading}
            scroll={{ x: 'max-content' }}
            className="shadow border rounded"
          />
        </div>

        <div className="col-12 col-md-2"></div>
      </div>
    </div>
  )
}

export default AttendedStudentList
